// O vocabulário compartilhado da aba "Inscritos": a fase do simulado, os
// rótulos de status e o pipeline puro (busca/filtro/ordenação/paginação/CSV)
// sobre a lista de inscrições. Resumo, Tabela, Toolbar e o modal de
// cancelamento leem só daqui, para que as quatro consequências de uma
// condição (design.md, Decisão 6) não possam divergir entre si.
//
// `RegistrationEntry`/`DerivedRegistrationStatus`/`RegistrationCancelReason`
// são os tipos canônicos de `sdk/@types/Simulado.ts` (task irmã
// `inscritos-types-and-service`, já mesclada) — re-exportados daqui para que
// quem monta a tabela/toolbar/modal importe de um só lugar.
import type {
  DerivedRegistrationStatus,
  RegistrationCancelHistoryEntry,
  RegistrationCancelReason,
  RegistrationEntry,
} from '../../../sdk/@types/Simulado';

export type {
  DerivedRegistrationStatus,
  RegistrationCancelHistoryEntry,
  RegistrationCancelReason,
  RegistrationEntry,
};

// ---- Ciclo de vida do simulado --------------------------------------------

export type Fase = 'antes' | 'durante' | 'depois';

/**
 * O único lugar deste diretório que compara o relógio atual contra o início
 * e o fim do simulado. Toda outra função aqui recebe `fase` já calculada em
 * vez de derivá-la de novo (design.md de `simulado-inscritos-tab`, Decisão
 * 6) — é o que garante que o Resumo, as colunas da tabela, a ação primária
 * da linha e o cancelamento habilitado nunca leiam relógios diferentes.
 *
 * Cortes fechados à esquerda: `antes` é só `agora < início`; `durante` cobre
 * `início <= agora < fim`; `depois` é `agora >= fim` (design.md de
 * `simulado-cancelamento-efetivo`, Decisão 1). Um `mocktestEnd` ausente,
 * vazio ou não-parseável cai no corte antigo de duas fases, nunca lança —
 * uma resposta de uma API que ainda não manda esse campo precisa continuar
 * renderizando como hoje.
 */
export function fase(mocktestStart: string, mocktestEnd: string | null | undefined, now: Date): Fase {
  const start = new Date(mocktestStart).getTime();
  const nowMs = now.getTime();
  const end = mocktestEnd ? new Date(mocktestEnd).getTime() : NaN;

  if (Number.isNaN(end)) {
    return nowMs < start ? 'antes' : 'depois';
  }
  if (nowMs < start) return 'antes';
  return nowMs < end ? 'durante' : 'depois';
}

// ---- Vocabulário de status --------------------------------------------------

// `desistiu` (API) não tem frame e nada no produto escreve esse valor hoje —
// mantido na tabela de rótulos/tons abaixo só para ela ser total sobre o tipo.

const STATUS_LABELS: Record<DerivedRegistrationStatus, string> = {
  concluido: 'Concluído',
  incompleto: 'Incompleto',
  nao_iniciou: 'Não iniciou',
  cancelado: 'Cancelado',
  desistiu: 'Desistiu',
};

// Tons em tokens, nunca hex/gray crus. `danger` não aparece neste arquivo —
// "Cancelado"/"Não iniciou" são estado, não erro, a mesma regra que /certame
// aplica a "Inativo". "Inscrito" usa `select` (nunca `accent-*`, reservado a
// ladrilho de ícone). "Concluído"/"Incompleto" podem usar `ok`/`warn`: a
// regra do produto só reserva vermelho, não essas duas famílias.
const STATUS_TONE_CLASSNAME: Record<DerivedRegistrationStatus, string> = {
  concluido: 'bg-ok/[0.12] text-ok-ink',
  incompleto: 'bg-warn/[0.12] text-warn-ink',
  nao_iniciou: 'bg-surface-muted text-ink-muted',
  cancelado: 'bg-surface-muted text-ink-muted',
  desistiu: 'bg-surface-muted text-ink-muted',
};

const INSCRITO_LABEL = 'Inscrito';
const INSCRITO_TONE_CLASSNAME = 'bg-select/[0.14] text-select-ink';

/**
 * `nao_iniciou` é a mesma linha, mas lida diferente antes do início: ainda
 * não faz sentido chamar alguém de "não iniciou" um simulado que ninguém
 * começou. Única exceção da tabela de rótulos/tons, resolvida num só lugar.
 */
function isInscrito(derivedStatus: DerivedRegistrationStatus, fase: Fase): boolean {
  return derivedStatus === 'nao_iniciou' && fase === 'antes';
}

export function statusLabel(derivedStatus: DerivedRegistrationStatus, fase: Fase): string {
  return isInscrito(derivedStatus, fase) ? INSCRITO_LABEL : STATUS_LABELS[derivedStatus];
}

export function statusToneClassName(derivedStatus: DerivedRegistrationStatus, fase: Fase): string {
  return isInscrito(derivedStatus, fase) ? INSCRITO_TONE_CLASSNAME : STATUS_TONE_CLASSNAME[derivedStatus];
}

// ---- Busca, filtro, ordenação, paginação -----------------------------------

export interface InscritosFilter {
  search?: string;
  status?: DerivedRegistrationStatus | 'todos';
}

export function filterInscritos(
  rows: RegistrationEntry[],
  filter: InscritosFilter,
): RegistrationEntry[] {
  const search = filter.search?.trim().toLowerCase();
  return rows.filter((row) => {
    if (search) {
      const matchesName = row.userName.toLowerCase().includes(search);
      const matchesEmail = row.userEmail.toLowerCase().includes(search);
      if (!matchesName && !matchesEmail) return false;
    }
    if (filter.status && filter.status !== 'todos' && row.derivedStatus !== filter.status) {
      return false;
    }
    return true;
  });
}

export type InscritoSortKey = 'nome' | 'data' | 'nota';
export type SortDirection = 'asc' | 'desc';

export function sortInscritos(
  rows: RegistrationEntry[],
  key: InscritoSortKey,
  direction: SortDirection = 'asc',
): RegistrationEntry[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    switch (key) {
      case 'nome':
        return sign * a.userName.localeCompare(b.userName, 'pt-BR');
      case 'data':
        return sign * (new Date(a.registryDate).getTime() - new Date(b.registryDate).getTime());
      case 'nota':
        return sign * (a.totalCorrect - b.totalCorrect);
      default:
        return 0;
    }
  });
}

export const INSCRITOS_PAGE_SIZE = 8;

export interface InscritosPage {
  items: RegistrationEntry[];
  /** 1-based, 0 quando `total` é 0. */
  from: number;
  /** 1-based, inclusive, 0 quando `total` é 0. */
  to: number;
  total: number;
}

export function paginateInscritos(rows: RegistrationEntry[], page: number): InscritosPage {
  const total = rows.length;
  // Clamped on both ends: a page number held by the caller can outlive a
  // filter that shrinks `rows`, and an unclamped `from` would then exceed
  // `to` (e.g. "Mostrando 793–792 de 48") instead of settling on the last
  // real page.
  const lastPage = Math.max(1, Math.ceil(total / INSCRITOS_PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), lastPage);
  const start = (safePage - 1) * INSCRITOS_PAGE_SIZE;
  const items = rows.slice(start, start + INSCRITOS_PAGE_SIZE);
  return {
    items,
    from: total === 0 ? 0 : start + 1,
    to: total === 0 ? 0 : start + items.length,
    total,
  };
}

// ---- Motivos de cancelamento -----------------------------------------------
// `RegistrationCancelReason` vem de `sdk/@types/Simulado.ts` (import no topo
// deste arquivo); aqui só o rótulo pt-BR de cada valor, na ordem fixa que o
// modal de cancelamento deve mostrar — "Outro" sempre por último
// (proposal.md, Suposição 2).

export interface CancelReasonOption {
  value: RegistrationCancelReason;
  label: string;
}

export const CANCEL_REASONS: CancelReasonOption[] = [
  { value: 'solicitacao_aluno', label: 'Solicitação do aluno' },
  { value: 'inscricao_duplicada', label: 'Inscrição duplicada' },
  { value: 'pagamento_nao_confirmado', label: 'Pagamento não confirmado' },
  { value: 'decisao_administrativa', label: 'Decisão administrativa' },
  { value: 'outro', label: 'Outro' },
];

const CANCEL_REASON_LABELS: Record<string, string> = Object.fromEntries(
  CANCEL_REASONS.map((option) => [option.value, option.label]),
);

const CANCEL_REASON_FALLBACK_LABEL = 'Motivo não informado';

/**
 * O único mapa de rótulos de motivo do módulo — derivado de `CANCEL_REASONS`,
 * nunca uma segunda tabela escrita à mão, para que um motivo renomeado mude
 * os dois lugares juntos (design.md, Decisão 3). Recebe `string`, não só
 * `RegistrationCancelReason`: o valor vem de uma resposta de API, e um
 * motivo que o contrato ainda não lista cai no rótulo neutro — nunca o
 * valor cru da API chega à tela.
 */
export function cancelReasonLabel(reason: string): string {
  return CANCEL_REASON_LABELS[reason] ?? CANCEL_REASON_FALLBACK_LABEL;
}

function flatCancellation(row: RegistrationEntry): RegistrationCancelHistoryEntry | undefined {
  if (!row.cancelReason || !row.canceledAt || !row.canceledBy) return undefined;
  return {
    cancelReason: row.cancelReason,
    cancelNote: row.cancelNote,
    canceledAt: row.canceledAt,
    canceledBy: row.canceledBy,
  };
}

/**
 * O back devolve `cancelHistory` do mais antigo para o mais recente
 * (`simulado-cancelamento-efetivo-api`, "Leitura administrativa dos
 * inscritos de um simulado") — por isso o mais recente é a última entrada
 * do array, nunca a primeira. Sem histórico, os quatro campos soltos da
 * linha já são o próprio cancelamento mais recente (o mesmo back os mantém
 * em sincronia); numa linha nunca cancelada nenhum dos dois existe, e a
 * função devolve `undefined` — a tela não reserva espaço para um
 * cancelamento que não aconteceu (design.md, Decisão 3).
 */
export function latestCancellation(
  row: RegistrationEntry,
): RegistrationCancelHistoryEntry | undefined {
  if (row.cancelHistory && row.cancelHistory.length > 0) {
    return row.cancelHistory[row.cancelHistory.length - 1];
  }
  return flatCancellation(row);
}

// ---- Disponibilidade de cancelamento ---------------------------------------
// Copy desta mudança (design.md, Decisão 3) — nenhum frame mostra uma linha
// já cancelada, então a justificativa de "já cancelada" é invenção própria,
// não fidelidade a um frame.

const JUSTIFICATION_WINDOW_CLOSED = 'Disponível apenas antes do início do simulado.';
const JUSTIFICATION_ALREADY_CANCELLED = 'Esta inscrição já está cancelada.';

export interface CancelAvailability {
  available: boolean;
  /** Presente só quando `available` é `false`. */
  justification?: string;
}

/**
 * O botão primário da linha, a entrada "Cancelar inscrição" do menu "⋯" e a
 * justificativa escrita ao lado dela são três leitores da mesma regra — por
 * isso a decisão mora aqui, não numa comparação escrita à mão dentro de
 * `InscritoRowActions` (design.md, Decisão 3), pelo mesmo raciocínio que já
 * pôs `fase` neste módulo. Recebe `fase` já calculada — não compara o
 * relógio. Já cancelada vence em qualquer fase que não seja `antes`: é a
 * justificativa mais específica das duas. `durante` trata a janela como já
 * fechada, igual a `depois` (design.md de `simulado-cancelamento-efetivo`,
 * Decisão 1) — por isso o corte é `fase !== 'antes'`, não uma comparação com
 * `'depois'`, para que uma quarta fase futura caia no lado seguro por
 * construção.
 */
export function cancelAvailability(row: RegistrationEntry, fase: Fase): CancelAvailability {
  if (row.derivedStatus === 'cancelado') {
    return { available: false, justification: JUSTIFICATION_ALREADY_CANCELLED };
  }
  if (fase !== 'antes') {
    return { available: false, justification: JUSTIFICATION_WINDOW_CLOSED };
  }
  return { available: true };
}

// ---- Formatação compartilhada (Acertos/Tempo) ------------------------------
//
// A mesma regra "— em vez de 0" que a tabela precisa (spec.md, "Inscrito que
// não iniciou") alimenta o CSV abaixo; exportadas para que a tabela (task
// separada) leia daqui em vez de reescrever a mesma regra.

export function formatAcertos(row: RegistrationEntry): string {
  return row.answeredCount === 0 ? '—' : `${row.totalCorrect}/${row.totalQuestions}`;
}

export function formatTempo(row: RegistrationEntry): string {
  if (row.answeredCount === 0) return '—';
  const totalSeconds = Math.max(0, Math.round(row.elapsedTime));
  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** Exportada para que `InscritosTable` leia a mesma formatação do CSV na coluna "Inscrito em". */
export function formatRegistryDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function csvField(value: string): string {
  return /["\r\n,]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Uma coluna por campo que a tabela mostra naquela fase — por isso recebe
 * `fase`, não só a lista: Acertos/Tempo somem do CSV só na fase `antes`,
 * nunca viram zero ou vazio (spec.md, "Exportar a lista em CSV"). `durante`
 * usa o mesmo conjunto de `depois` — a prova já começou, os números existem
 * (design.md de `simulado-cancelamento-efetivo`, Decisão 1) — por isso o
 * corte é `fase !== 'antes'`, não uma comparação com `'depois'`.
 *
 * As três colunas de cancelamento ficam sempre no cabeçalho, em toda fase e
 * mesmo sem nenhuma linha cancelada filtrada — um arquivo cujas colunas
 * mudam com o filtro não é comparável com outro (spec.md, "O motivo vai no
 * arquivo"). Uma linha ativa as deixa vazias; `latestCancellation` decide o
 * que entra nelas, e `cancelReasonLabel` é o único tradutor do motivo —
 * nunca o valor cru da API.
 */
export function toCsv(rows: RegistrationEntry[], fase: Fase): string {
  const columns = [
    ...(fase !== 'antes'
      ? ['Nome', 'E-mail', 'Inscrito em', 'Status', 'Acertos', 'Tempo']
      : ['Nome', 'E-mail', 'Inscrito em', 'Status']),
    'Motivo do cancelamento',
    'Cancelado em',
    'Observação',
  ];

  const lines = rows.map((row) => {
    const base = [row.userName, row.userEmail, formatRegistryDate(row.registryDate), statusLabel(row.derivedStatus, fase)];
    const cells = fase !== 'antes' ? [...base, formatAcertos(row), formatTempo(row)] : base;

    const cancellation = latestCancellation(row);
    const cancelCells = cancellation
      ? [cancelReasonLabel(cancellation.cancelReason), formatRegistryDate(cancellation.canceledAt), cancellation.cancelNote ?? '']
      : ['', '', ''];

    return [...cells, ...cancelCells].map(csvField).join(',');
  });

  return [columns.join(','), ...lines].join('\n');
}
